import { useState, useEffect, useCallback } from "react";
import {
  Users,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Brain,
  TrendingUp,
  Clock,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Card } from "../components/UI";
import { PageLoader } from "../components/LoadingSpinner";
import { ErrorAlert, InlineError } from "../components/ErrorAlert";
import dashboardApi from "../api/dashboard";

const PRIORITY_COLORS = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
  low: "bg-green-100 text-green-700 border-green-200",
};

const PRIORITY_LABEL = { high: "Tinggi", medium: "Sedang", low: "Rendah" };

const PIE_COLORS = ["#dc2626", "#d97706", "#16a34a"];

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff} detik yang lalu`;
  if (diff < 3600) return `${Math.floor(diff / 60)} menit yang lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam yang lalu`;
  return `${Math.floor(diff / 86400)} hari yang lalu`;
}

export default function PopulationAnalysis() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [analyzeError, setAnalyzeError] = useState("");

  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await dashboardApi.getPopulationAnalysis();
      setData(res.data?.data ?? null);
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memuat analisis populasi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setAnalyzeError("");
    try {
      const res = await dashboardApi.analyzePopulation();
      setData(res.data?.data ?? null);
    } catch (err) {
      setAnalyzeError(
        err.response?.data?.message || "Gagal menjalankan analisis",
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const pieData = data
    ? [
        { name: "High Risk", value: data.high_risk_count ?? 0 },
        { name: "Moderate Risk", value: data.moderate_risk_count ?? 0 },
        { name: "Low Risk", value: data.low_risk_count ?? 0 },
      ].filter((d) => d.value > 0)
    : [];

  if (loading)
    return (
      <div className="p-6">
        <PageLoader message="Memuat analisis populasi..." />
      </div>
    );
  if (error)
    return (
      <div className="p-6">
        <ErrorAlert message={error} onRetry={fetchAnalysis} />
      </div>
    );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-display font-800 text-2xl text-gray-900">
            Analisis Populasi EPDS
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {data?.analyzed_at
              ? `Terakhir dianalisis: ${timeAgo(data.analyzed_at)}`
              : "Belum pernah dianalisis"}
          </p>
        </div>
        <button
          onClick={handleAnalyze}
          disabled={analyzing}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-800 hover:bg-primary-900 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${analyzing ? "animate-spin" : ""}`} />
          {analyzing ? "Menganalisis..." : "Analisis Ulang"}
        </button>
      </div>

      <InlineError message={analyzeError} />

      {/* Empty state */}
      {!data ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Brain className="w-14 h-14 mb-4 opacity-25" />
          <p className="text-base font-semibold text-gray-500 mb-1">
            Belum ada analisis
          </p>
          <p className="text-sm mb-5">
            Klik "Analisis Ulang" untuk memulai analisis populasi
          </p>
          <button
            onClick={handleAnalyze}
            disabled={analyzing}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-800 text-white text-sm font-semibold rounded-xl hover:bg-primary-900 disabled:opacity-60"
          >
            <RefreshCw
              className={`w-4 h-4 ${analyzing ? "animate-spin" : ""}`}
            />
            {analyzing ? "Menganalisis..." : "Mulai Analisis"}
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              {
                label: "Total Users",
                value: data.total_users,
                icon: Users,
                color: "text-primary-700",
                bg: "bg-primary-50",
              },
              {
                label: "High Risk",
                value: data.high_risk_count,
                icon: AlertTriangle,
                color: "text-red-600",
                bg: "bg-red-50",
              },
              {
                label: "Moderate Risk",
                value: data.moderate_risk_count,
                icon: AlertTriangle,
                color: "text-yellow-600",
                bg: "bg-yellow-50",
              },
              {
                label: "Low Risk",
                value: data.low_risk_count,
                icon: CheckCircle,
                color: "text-green-600",
                bg: "bg-green-50",
              },
              {
                label: "Avg EPDS Score",
                value: parseFloat(data.avg_epds_score).toFixed(1),
                icon: TrendingUp,
                color: "text-purple-600",
                bg: "bg-purple-50",
              },
              {
                label: "High Risk %",
                value: `${parseFloat(data.high_risk_percentage).toFixed(0)}%`,
                icon: TrendingUp,
                color: "text-red-600",
                bg: "bg-red-50",
              },
            ].map((s) => (
              <Card key={s.label} className="p-4 flex flex-col gap-2">
                <div
                  className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center`}
                >
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <div>
                  <p className={`font-display font-800 text-xl ${s.color}`}>
                    {s.value}
                  </p>
                  <p className="text-xs text-gray-400">{s.label}</p>
                </div>
              </Card>
            ))}
          </div>

          {/* Chart + Insight */}
          <div className="grid md:grid-cols-2 gap-5">
            {/* Donut chart */}
            <Card className="p-5">
              <h3 className="font-display font-700 text-gray-900 mb-4">
                Distribusi Risiko
              </h3>
              {pieData.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
                  Tidak ada data risiko
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: "12px",
                        border: "1px solid #e5e7eb",
                        fontSize: 12,
                      }}
                      formatter={(value, name) => [value, name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}

              {/* Stacked bar */}
              {data.total_users > 0 && (
                <div className="mt-2">
                  <div className="w-full h-3 rounded-full overflow-hidden flex">
                    {data.high_risk_count > 0 && (
                      <div
                        className="bg-red-500 transition-all"
                        style={{
                          width: `${(data.high_risk_count / data.total_users) * 100}%`,
                        }}
                      />
                    )}
                    {data.moderate_risk_count > 0 && (
                      <div
                        className="bg-yellow-400 transition-all"
                        style={{
                          width: `${(data.moderate_risk_count / data.total_users) * 100}%`,
                        }}
                      />
                    )}
                    {data.low_risk_count > 0 && (
                      <div
                        className="bg-green-500 transition-all"
                        style={{
                          width: `${(data.low_risk_count / data.total_users) * 100}%`,
                        }}
                      />
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>🔴 High</span>
                    <span>🟡 Moderate</span>
                    <span>🟢 Low</span>
                  </div>
                </div>
              )}
            </Card>

            {/* Insight */}
            {data.insight && (
              <Card className="p-5 border-l-4 border-l-primary-700">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-5 h-5 text-primary-700" />
                  <h3 className="font-display font-700 text-gray-900">
                    Insight AI
                  </h3>
                </div>
                <div className="bg-primary-50 rounded-xl p-4">
                  <p className="text-sm text-primary-900 leading-relaxed">
                    {data.insight}
                  </p>
                </div>
                {data.analyzed_at && (
                  <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-400">
                    <Clock className="w-3.5 h-3.5" />
                    {new Date(data.analyzed_at).toLocaleString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Recommendations */}
          {data.recommendation?.length > 0 && (
            <Card className="p-5">
              <h3 className="font-display font-700 text-gray-900 mb-4">
                Rekomendasi Tindakan
              </h3>
              <div className="space-y-3">
                {data.recommendation.map((rec, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl"
                  >
                    <div className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-600">
                      {i + 1}
                    </div>
                    <p className="text-sm text-gray-700 flex-1 leading-relaxed">
                      {rec.action}
                    </p>
                    <span
                      className={`text-xs font-bold px-2.5 py-0.5 rounded-full border flex-shrink-0 ${PRIORITY_COLORS[rec.priority] || PRIORITY_COLORS.low}`}
                    >
                      {PRIORITY_LABEL[rec.priority] ?? rec.priority}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
